<template>
  <div class="page">
    <!-- 顶部导航栏：返回按钮 + 标题 -->
    <div class="nav-bar">
      <div class="nav-back" @click="goBack">
        <!-- 返回箭头 -->
        <svg viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
      <div class="nav-title">玩法规则</div>
    </div>

    <!-- 彩票类型切换标签：循环展示 lotteryTypeList，默认选中第一个（快乐8） -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 中间提示语：仅供参考 -->
    <div class="notice-bar">玩法规则仅供参考，具体以福彩、体彩官网为准</div>

    <!-- 最下面：当前类型的规则内容 -->
    <div class="rules-wrap">
      <!-- 已提供规则的类型：按板块循环展示（投注/设奖/中奖） -->
      <div v-if="currentRules.length" class="rules-card">
        <div v-for="section in currentRules" :key="section.title" class="rules-section">
          <!-- 板块标题：投注 / 设奖 / 中奖 -->
          <div class="section-title">{{ section.title }}</div>
          <!-- 该板块下的条款：普通文字段落 -->
          <template v-if="!section.type">
            <p v-for="(item, i) in section.items" :key="i" class="rule-text">{{ item }}</p>
          </template>
          <!-- 奖金对照表板块：表格形式 -->
          <template v-else-if="section.type === 'table'">
            <table class="prize-table">
              <thead>
                <tr>
                  <th v-for="(col, ci) in section.columns" :key="ci">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in section.rows" :key="ri">
                  <td v-for="(cell, cj) in row" :key="cj">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <!-- 奖金对照图片板块：整图展示，宽度撑满 -->
          <template v-else-if="section.type === 'image'">
            <img :src="section.src" class="prize-image" :alt="section.title" />
          </template>
        </div>
      </div>

      <!-- 规则暂未提供的类型：显示占位提示（后续规则提供后补进 rulesMap 即可） -->
      <div v-else class="rules-card rules-empty">
        {{ typeLabel }}玩法规则暂未提供，敬请期待
      </div>
    </div>
  </div>
</template>

<script>
// 彩票类型枚举统一从工具文件引入，保证与购买/兑奖页一致
import { lotteryTypeList, lotteryTypeLabel } from '@/utils/lottery'
// 快乐8中奖奖金对照表图片
import klbPrizeTableImg from '@/assets/prize/klb-prize-table.png'
// 双色球中奖奖金对照表图片
import ssqPrizeTableImg from '@/assets/prize/ssq-prize-table.png'
// 大乐透中奖奖金对照表图片
import dltPrizeTableImg from '@/assets/prize/dlt-prize.table.png'
// 七乐彩中奖奖金对照表图片
import qlcPrizeTableImg from '@/assets/prize/qlc-prize-table.png'

export default {
  name: 'GameRulesView',
  data() {
    return {
      // 彩票类型枚举（快乐8/双色球/.../排列五），循环渲染成顶部标签
      typeList: lotteryTypeList,
      // 当前选中的彩票类型，默认第一个：双色球
      lotteryType: 'ssq',
      // 各彩票类型的规则内容：key 为类型编码，value 为板块数组（标题 + 条款列表）
      // section.type 默认不写，用 items 文字段落渲染；设为 'table' 时 columns + rows 渲染奖金对照表
      rulesMap: {
        // ==================== 快乐8 ====================
        klb: [
          {
            title: '投注',
            items: [
              '第一条 快乐8游戏是指从1至80共八十个号码中任意选择一至十个号码进行投注，每一组一个至十个号码的组合称为一注彩票，每注金额人民币2元。',
              '第二条 快乐8游戏共包括"选一"、"选二"、"选三"、"选四"、"选五"、"选六"、"选七"、"选八"、"选九"和"选十"十种玩法。',
              '第三条 快乐8游戏按期销售，每天销售一期，期号按当年开奖次数排序',
              '第四条 投注号码可以自选也可以机选。自选号码投注是指由购买者指定投注号码的投注；机选号码投注是指由投注机随机产生投注号码的投注。',
              '第五条 快乐8游戏各玩法均支持单式投注。"选一"至"选十"单式投注的号码个数分别为一个至十个。投注时从八十个号码中选出对应玩法要求的号码个数作为一注彩票。',
              '第六条 快乐8游戏各玩法均支持复式投注和胆拖投注。复式投注是指所选号码个数超过单式投注的号码个数，所选号码可组合为每一种单式投注方式的多注彩票的投注。胆拖投注指先选取少于单式投注号码个数的号码作为每注都有的胆码，再选取除胆码以外的号码作为拖码，胆码与拖码个数之和必须多于单式投注号码个数，由胆码与拖码的每一种组合按单式投注方式组成多注彩票的投注。',
              '第七条 购买者可对其选定的投注号码进行多倍投注，投注倍数范围为2-15倍。单张彩票的投注注数不得超过10000注。',
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 快乐8游戏按当期实际销售额的58%、30%和12%分别计提彩票奖金、彩票公益金和彩票发行费。彩票奖金分为当期奖金和调节基金，其中，57%为当期奖金，1%为调节基金',
              '第二条 快乐8游戏按不同玩法设奖，“选九”玩法中“选九中九”奖级、“选十”玩法中“选十中十”奖级采用浮动奖金设奖，其他玩法及奖级均采用固定奖金设奖',
            ]
          },
          {
            title: '奖金对照表',
            type: 'image',
            src: klbPrizeTableImg
          }
        ],
        // ==================== 双色球 ====================
        ssq: [
          {
            title: '投注',
            items: [
              '第一条 双色球投注区分为红色球号码区和蓝色球号码区，红色球号码区由1-33共三十三个号码组成，蓝色球号码区由1-16共十六个号码组成。投注时选择6个红色球号码和1个蓝色球号码组成一注进行单式投注，每注金额人民币2元。',
              '第二条 购买者可在各省福彩机构设置的销售网点投注。投注号码经投注机打印出对奖凭证，交购买者保存，此对奖凭证即为双色球彩票。',
              '第三条 购买者可选择机选号码投注、自选号码投注。机选号码投注是指由投注机随机产生投注号码进行投注，自选号码投注是指将购买者选定的号码输入销售终端进行投注。',
              '第四条 购买者可选择复式投注。复式投注是指所选号码个数超过单式投注的号码个数，所选号码可组合为每一种单式投注方式的多注彩票的投注。具体规定如下：(一)红色球号码复式：是指从红色球号码中选择7个号码以上(含7个号码)，从蓝色球号码中选择1个号码，组合为多注单式投注号码的投注；(二)蓝色球号码复式：是指从红色球号码中选择6个号码，从蓝色球号码中选择2个号码以上(含2个号码)，组合为多注单式投注号码的投注；(三)全复式：是指从红色球号码中选择7个号码以上(含7个号码)，从蓝色球号码中选择2个号码以上(含2个号码)，组合为多注单式投注号码的投注。',
              '第五条 购买者可选择胆拖投注。胆拖投注是指选择少于单式投注号码个数的号码作为每注都有的号码作为胆码，再选取除胆码以外的号码作为拖码，由胆码和拖码组合成多注投注，胆拖投注包括两种形式：(一)单式胆拖：从红色球号码中选择1至5个号码为胆码，再选取除胆码以外的号码作为拖码(胆码和拖码之和必须大于等于7个号码)，胆码和拖码组成每6个号码为一组的红色球号码，从蓝色球号码中选择1个号码，蓝色球号码和红色球号码一起组合为多注单式投注号码的投注。(二)复式胆拖：从红色球号码中选择1至5个号码为胆码，再选取除胆码以外的号码作为拖码(胆码和拖码之和必须大于等于7个号码)，胆码和拖码组成每6个号码为一组的红色球号码，从蓝色球号码中选择2个号码以上(含2个号码)，蓝色球号码和红色球号码一起组合为多注单式投注号码的投注。',
              '第六条 购买者可对其选定的投注号码进行多倍投注，投注倍数范围为2-99倍。单张彩票的投注金额最高不得超过20000元。',
              '第七条 双色球按期销售，每周销售三期，期号以开奖日界定，按日历年度编排。',
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 双色球按当期销售额的51%、13%和36%分别计提彩票奖金、彩票发行费和彩票公益金。彩票奖金分为当期奖金和调节基金，其中，49%为当期奖金，2%为调节基金。',
              '第二条 双色球采取全国统一奖池计奖。',
              '第三条 双色球奖级设置分为高奖级和低奖级，一等奖和二等奖为高奖级，三至六等奖为低奖级。当期奖金减去当期低奖级奖金为当期高奖级奖金。各奖级和奖金规定如下：一等奖：当奖池资金低于1亿元时，奖金总额为当期高奖级奖金的75%与奖池中累积的资金之和，单注奖金按注均分，单注最高限额封顶500万元。当奖池资金高于1亿元(含)时，奖金总额包括两部分，一部分为当期高奖级奖金的55%与奖池中累积的资金之和，单注奖金按注均分，单注最高限额封顶500万元；另一部分为当期高奖级奖金的20%，单注奖金按注均分，单注最高限额封顶500万元。二等奖：奖金总额为当期高奖级奖金的25%，单注奖金按注均分，单注最高限额封顶500万元。三等奖：单注奖金固定为3000元。四等奖：单注奖金固定为200元。五等奖：单注奖金固定为10元。六等奖：单注奖金固定为5元。',
            ]
          },
          {
            title: '奖金对照表',
            type: 'image',
            src: ssqPrizeTableImg
          }
        ],
        // ==================== 七乐彩 ====================
        qlc: [
          {
            title: '投注',
            items: [
              '第一条 七乐彩采用组合式玩法，从01-30共30个号码中选择7个号码组合为一注投注号码。每注金额人民币2元。',
              '第二条 七乐彩每周销售三期，期号以开奖日界定，按日历年度编排。',
              '第三条 七乐彩投注方法分为自选号码投注和机选号码投注，投注方式分为单式投注、复式投注、胆拖投注和多倍投注。',
              '第四条 自选号码投注是指由投注者自行选定投注号码的投注；机选号码投注是指由投注机为投注者随机产生投注号码的投注。',
              '第五条 单式投注是从30个号码中选择7个号码，组合为一注投注号码的投注；复式投注是从30个号码中选择8-16个号码，将每7个号码的组合方式都作为一注投注号码的多注投注；胆拖投注是在30个号码中选择1至6个号码作为每注都有的胆码，再补充其它不同的号码，进行每7个号码为一组的多注投注；多倍投注是指同样的投注号码进行多注投注。'
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 七乐彩设奖奖金为销售总额的50%，其中当期奖金为销售总额的49%，调节基金为销售总额的1%。',
              '第二条 七乐彩当期奖金设七个奖等，一至三等奖为高奖等，四至七等奖为低奖等。高奖等采用浮动设奖，低奖等采用固定设奖。当期奖金减去当期低奖等奖金为当期高奖等奖金，具体设奖如下：一等奖：奖金总额为当期高奖等奖金的70%；二等奖：奖金总额为当期高奖等奖金的10%；三等奖：奖金总额为当期高奖等奖金的20%；四等奖：单注奖金额固定为200元；五等奖：单注奖金额固定为50元；六等奖：单注奖金额固定为10元；七等奖：单注奖金额固定为5元。',
              '第三条 七乐彩单注奖金的最高限额为500万元。',
              '第四条 七乐彩设立奖池，奖池由未中出的高奖等奖金和超出单注奖金封顶限额部分的奖金组成。奖池与当期奖金中用于一等奖的部分合并颁发一等奖奖金。',
              '第五条 调节基金包括按销售总额1%提取部分、弃奖奖金、浮动奖奖金按元取整后的余额。调节基金用于设置特别奖、调节浮动奖奖金、支付各种不可预见情况下的奖金支出风险。',
              '第六条 若当期高奖等单注奖额低于其次奖等单注奖额的二倍，应保证高奖等的单注奖额为其次奖等单注奖额的二倍，资金来源由调节基金补足，调节基金不足时由发行经费垫支。'
            ]
          },
          {
            title: '奖金对照表',
            type: 'image',
            src: qlcPrizeTableImg
          }
        ],
        // ==================== 大乐透 ====================
        dlt: [
          {
            title: '投注',
            items: [
              '第一条 超级大乐透基本投注是指从前区号码中任选五个号码，并从后区号码中任选两个号码的组合进行投注。其中，前区号码由01-35共三十五个号码组成，后区号码由01-12共十二个号码组成。每注基本投注金额人民币2元。购买者在基本投注的基础上，可对购买的每注号码进行一次追加投注，每注追加投注金额人民币1元。',
              '第二条 购买者可以进行复式投注。复式投注是指所选号码个数超过基本投注的号码个数，所选号码可组合为每一种基本投注方式的多注彩票的投注。复式投注包括三种形式：(一)前区复式：前区选取六个及以上号码，后区选取两个号码；(二)后区复式：前区选取五个号码，后区选取三个及以上号码；(三)双区复式：前区选取六个及以上号码，后区选取三个及以上号码。',
              '第三条 购买者可以进行胆拖投注。胆拖投注是指选择少于基本投注号码个数的号码作为每注都有的号码作为胆码，再选取除胆码以外的号码作为拖码，由胆码和拖码组合成多注投注，胆拖投注包括三种形式：(一)前区胆拖：从01-35中选取一至四个号码为胆码，再选取除胆码以外的号码作为拖码，胆码和拖码组成前区号码(其数量之和必须等于或多于六个号码)，并从01-12中选取两个号码为后区号码。(二)后区胆拖：从01-35中选取五个号码为前区号码，并从01-12中选取一个号码为胆码，再选取除胆码以外的两个以上(含两个)的号码为拖码，胆码和拖码组成后区号码。(三)双区胆拖：从01-35中选取一至四个号码为胆码，再选取除胆码以外的号码作为拖码，胆码和拖码组成前区号码(其数量之和必须等于或多于六个号码)；并从01-12中选取一个号码为胆码，再选取除胆码以外的两个以上(含两个)的号码为拖码，胆码和拖码组成后区号码。',
              '第四条 复式投注和胆拖投注均可按注进行追加投注。',
              '第五条 购买者可对其选定的结果进行多倍投注，投注倍数范围为2-99倍。单张彩票基本投注的最大投注金额不超过20000元，基本投注加追加投注的最大投注金额不超过30000元。',
              '第六条 超级大乐透按期销售，每周销售三期，期号以开奖日界定，按日历年度编排。',
              '第七条 购买者可在各省体彩机构设置的销售网点投注。投注号码经投注机打印出的对奖凭证，交购买者保存，此对奖凭证即为超级大乐透彩票。',
              '第八条 投注者可选择机选号码投注、自选号码投注。机选号码投注是指由投注机随机产生投注号码进行投注，自选号码投注是指将购买者选定的号码输入投注机进行投注。'
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 超级大乐透按当期销售总额的51%、13%、36%分别计提彩票奖金、彩票发行费和彩票公益金。彩票奖金分为当期奖金和调节基金，其中，49%为当期奖金，2%为调节基金。',
              '第二条 超级大乐透共设九个奖级，一、二等奖为浮动奖，三、四、五、六、七、八、九等奖为固定奖。各奖级和奖金规定如下：一等奖：当奖池资金低于1亿元时，奖金总额为当期奖金额减去固定奖总额后的78%与奖池中累积的奖金之和，单注奖金按注均分，单注最高限额封顶500万元。当奖池资金高于1亿元(含)且低于8亿元时，奖金总额包括两部分，第一部分为当期奖金额减去固定奖总额后的58%与奖池中累积的奖金之和，单注奖金按注均分，单注最高限额封顶500万元；第二部分为当期奖金额减去固定奖总额后的20%，单注奖金按注均分，单注最高限额封顶500万元。当奖池资金高于8亿元(含)时，奖金总额包括两部分，第一部分为当期奖金额减去固定奖总额后的28%与奖池中累积的奖金之和，单注奖金按注均分，单注最高限额封顶500万元；第二部分为当期奖金额减去固定奖总额后的50%，单注奖金按注均分，单注最高限额封顶500万元。二等奖：奖金总额为当期奖金额减去固定奖总额后的22%，单注奖金按注均分，单注最高限额封顶500万元。三等奖：单注奖金固定为10000元。四等奖：单注奖金固定为3000元。五等奖：单注奖金固定为300元。六等奖：单注奖金固定为200元。七等奖：单注奖金固定为100元。八等奖：单注奖金固定为15元。九等奖：单注奖金固定为5元。',
              '第三条 追加投注仅参与浮动奖的奖金分配。如追加投注中得浮动奖，则追加投注单注奖金为当期基本投注对应单注奖金的80%。',
              '第四条 浮动奖级单注奖金根据该奖级基本投注与追加投注中奖数量按比例分配。',
              '第五条 超级大乐透设置奖池，奖池由未中出的浮动奖奖金和超出浮动奖单注奖金封顶限额部分的奖金组成。奖池与当期奖金中用于一等奖的部分及调节基金转入部分合并支付一等奖奖金。',
              '第六条 调节基金包括按销售总额的2%提取部分、浮动奖奖金按元取整后的余额和逾期未退票的票款。调节基金专项用于支付各种不可预见情况下的奖金支出风险、调节浮动奖奖金以及设立特别奖。',
              '第七条 若当期奖金额不足以支付固定奖总额时，不足部分从调节基金中支付。若调节基金不足时，用彩票兑奖周转金垫支。',
              '第八条 浮动奖按照该奖级实际中奖注数平均分配该奖级奖金。其中，一等奖设置单注最低奖金，为单注奖金封顶金额的3%。同时，当浮动奖上一奖级单注奖金低于下一奖级单注奖金的两倍且低于500万元时，上一奖级单注奖金补足至下一奖级单注奖金的两倍并不高于500万元。所需资金从调节基金中支付，若调节基金不足时，用彩票兑奖周转金垫支。',
              '第九条 超级大乐透设置单期最大返奖总额，若当期应付奖金超出最大返奖总额，则各奖级单注奖金(含浮动奖单注最低奖金)相应调整。',
              '第十条 在出现彩票兑奖周转金垫支情况下，当调节基金有资金滚入时优先偿还垫支的彩票兑奖周转金。'
            ]
          },
          {
            title: '奖金对照表',
            type: 'image',
            src: dltPrizeTableImg
          }
        ],
        // ==================== 七星彩 ====================
        qxc: [
          {
            title: '投注',
            items: [
              '第一条 七星彩每期开出一个7位数作为中奖号码，前区6个数字+后区1个数字组成一注，每注金额人民币2元。前区每位号码为0-9，后区号码为0-14。',
              '第二条 七星彩每周二、五、日21:25开奖，按期销售，期号以开奖日界定，按日历年度编排。',
              '第三条 购买者可在各省体彩机构设置的销售网点投注。投注号码经投注机打印出的对奖凭证，交购买者保存，此对奖凭证即为七星彩彩票。',
              '第四条 投注者可选择机选号码投注、自选号码投注。机选号码投注是指由投注机随机产生投注号码进行投注，自选号码投注是指将购买者选定的号码输入投注机进行投注。',
              '第五条 购买者可对其选定的结果进行多倍投注，投注倍数范围为2-99倍。'
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 七星彩设六个奖级，一等奖和二等奖为浮动奖，三等奖至六等奖为固定奖。',
              '第二条 一等奖单注奖金最高封顶500万元，二等奖为浮动奖，单注奖金按注均分。',
              '三等奖：单注奖金固定为3000元。四等奖：单注奖金固定为500元。五等奖：单注奖金固定为30元。六等奖：单注奖金固定为5元。'
            ]
          },
          {
            title: '中奖',
            type: 'table',
            columns: ['奖级', '中奖条件', '奖金'],
            rows: [
              ['一等奖', '前区6码+后区1码', '浮动·最高500万'],
              ['二等奖', '前区6码', '浮动'],
              ['三等奖', '前区任意5码+后区1码', '3000元'],
              ['四等奖', '前区任意5码 或 前4码+后1码', '500元'],
              ['五等奖', '前区任意4码 或 前3码+后1码', '30元'],
              ['六等奖', '前区任意3码 或 前2码+后1码 或 前1码+后1码 或 后区1码', '5元']
            ]
          }
        ],
        // ==================== 排列五 ====================
        plw: [
          {
            title: '投注',
            items: [
              '第一条 购买"排列5"时，由购买者从00000-99999的数字中选取1个5位数为投注号码进行投注。',
              '第二条 购买者可在全国各省(区、市)体育彩票管理中心设置的投注站进行投注。投注号码可由投注机随机产生，也可通过投注单将购买者选定的号码输入投注机确定。投注号码经系统确认后打印出的对奖凭证即为"排列5"电脑体育彩票，交购买者保存。',
              '第三条 "排列5"每注2元人民币。彩票不记名、不挂失，不返还本金，不流通使用。'
            ]
          },
          {
            title: '设奖',
            items: [
              '第一条 "排列5"设1个奖级，为固定奖。',
              '第二条 奖金分配：一等奖，单注固定奖金100000元。'
            ]
          },
          {
            title: '中奖',
            items: [
              '第一条 所购彩票与开奖结果对照，符合以下情况即为中奖。',
              '一等奖，所选号码与中奖号码全部相同且顺序一致。例如：中奖号码为43751，则排列5的中奖结果为：43751。'
            ]
          }
        ]
      }
    }
  },
  computed: {
    // 当前类型的规则板块：没有提供规则的类型返回空数组，模板里显示占位提示
    currentRules() {
      return this.rulesMap[this.lotteryType] || []
    },
    // 当前类型的中文名，用于占位提示文案
    typeLabel() {
      return lotteryTypeLabel(this.lotteryType)
    }
  },
  methods: {
    // 切换彩票类型标签
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
    },

    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
}

/* ===== 顶部导航栏 ===== */
.nav-bar {
  position: relative;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-back {
  position: absolute;
  left: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-back svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-title {
  color: #fff;
  font-size: 16px;
}

/* ===== 彩票类型标签栏：与兑奖页一致，一屏最多 4 个，可横向滑动 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 中间提示条：仅供参考 ===== */
.notice-bar {
  margin: 12px 12px 0;
  padding: 10px 12px;
  background: #eef0fb;
  border-radius: 8px;
  text-align: center;
  font-size: 13px;
  color: #909399;
}

/* ===== 规则内容区 ===== */
.rules-wrap {
  padding: 12px;
}

.rules-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 16px;
}

.rules-section + .rules-section {
  /* 板块之间留出间距（第一个板块不加） */
  margin-top: 24px;
}

/* 板块标题：居中加粗，如 投注 / 设奖 / 中奖 */
.section-title {
  text-align: center;
  font-size: 17px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 14px;
}

/* 条款正文：两端对齐，行距放宽方便阅读 */
.rule-text {
  font-size: 14px;
  line-height: 1.8;
  color: #303133;
  text-align: justify;
  margin: 0 0 12px;
}

.rule-text:last-child {
  margin-bottom: 0;
}

/* 规则暂未提供时的占位提示 */
.rules-empty {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 50px 16px;
}

/* ===== 奖金对照表 ===== */
.prize-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.prize-table th {
  background: #667eea;
  color: #fff;
  font-weight: bold;
  padding: 8px 6px;
  text-align: center;
  border: 1px solid #667eea;
}

.prize-table td {
  padding: 7px 6px;
  text-align: center;
  border: 1px solid #ebeef5;
  color: #303133;
}

/* 奇数行背景色区分 */
.prize-table tbody tr:nth-child(odd) {
  background: #fafbfc;
}

/* 选十中十浮动奖金高亮 */
.prize-table td.highlight {
  color: #e6a23c;
  font-weight: bold;
}

/* ===== 奖金对照图片：宽度撑满卡片，保持原始比例 ===== */
.prize-image {
  display: block;
  width: 100%;
  border-radius: 8px;
}
</style>
